import React from "react";
import type { ApolloClient } from "@apollo/client";
import { PendingAdminApproval } from "statics/payrollIntegrationStates";
import { OverlayTrigger, Popover } from "react-bootstrap";
import Button from "components/Button";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { connect, type ConnectedProps } from "react-redux";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { adminConstants } from "actions/types";
import { toast } from "react-toastify";
import { approvePayrollIntegration } from "actions/adminActions";
import type { ApprovePayrollIntegrationSetupMutation } from "types/graphql";
import type { RootState } from "types/state";

// The mutation payload the service resolves to (`string | null`); passed to
// `onSuccess` after a successful activation.
type ApprovePayrollIntegrationResult =
  ApprovePayrollIntegrationSetupMutation["approvePayrollIntegrationSetup"];

const loadingSelector = createLoadingSelector(
  adminConstants.APPROVE_PAYROLL_INTEGRATION
);

const errorSelector = createErrorSelector(
  adminConstants.APPROVE_PAYROLL_INTEGRATION
);

const mapStateToProps = (state: RootState) => ({
  isActivating: loadingSelector(state),
  error: errorSelector(state),
});

const mapDispatchToProps = {
  approvePayrollIntegration,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  companyId?: string;
  status?: string;
  onSuccess?: (state: ApprovePayrollIntegrationResult) => void;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

export const PayrollIntegrationStatusField = ({
  companyId,
  status,
  approvePayrollIntegration,
  client,
  isActivating,
  error,
  onSuccess,
}: Props) => {
  if (status !== PendingAdminApproval) return <span>{status}</span>;
  return (
    <>
      {status} -{" "}
      <OverlayTrigger
        rootClose
        trigger="click"
        placement="bottom"
        overlay={
          <Popover className="action-popover">
            <Popover.Body>
              <p>Are you sure you want to activate this integration?</p>
              <div>
                <Button
                  type="button"
                  color="cancel"
                  name="cancel"
                  btnLabel="Cancel"
                  onClick={() => document.body.click()}
                  size="sm"
                />
                <Button
                  type="button"
                  color="primary"
                  name="submit"
                  btnLabel="Activate"
                  withArrow={true}
                  loading={isActivating}
                  onClick={async () => {
                    // dispatchFullLifecycle resolves to the success action
                    // ({ type, data }) or a failure action ({ type, error }) with
                    // no `data`; the original destructures `data` assuming success.
                    // Cast at the boundary as in ReferralForm's fetchOwners.
                    const { data: state } = (await approvePayrollIntegration(
                      client as ApolloClient<object>,
                      {
                        // companyId is structurally optional on OwnProps but
                        // always supplied; the GraphQL variable is required.
                        companyId: companyId as string,
                      }
                    )) as { data: ApprovePayrollIntegrationResult };
                    if (error) {
                      toast.error(error);
                      document.body.click();
                      return;
                    }

                    toast.success("Integration activated successfully");
                    // onSuccess is optional on OwnProps but invoked
                    // unconditionally here — preserve the original throw-on-undefined.
                    onSuccess!(state);
                    document.body.click();
                  }}
                  size="sm"
                />
              </div>
            </Popover.Body>
          </Popover>
        }
      >
        <span
          style={{
            fontSize: 12,
            color: "#0cb7c4",
          }}
        >
          Activate Integration
        </span>
      </OverlayTrigger>
    </>
  );
};

export default connector(withApollo<Props>(PayrollIntegrationStatusField));
